import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Calendar, MapPin, Users, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

function UserDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
    fetchBookings();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      toast.error('Failed to fetch user data');
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await api.get('/bookings/user');
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled successfully');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background">
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
              alt="Hasaka Tours & Travels"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white rounded-sm px-6"
              >
                Admin Panel
              </Button>
            )}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-slate-600 hover:text-red-600 font-medium text-sm uppercase tracking-wider"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="dashboard-heading">
            Welcome, {user?.name}!
          </h2>
          <p className="text-slate-600 font-public-sans text-lg">Manage your bookings and travel history</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading bookings...</p>
            </div>
          </div>
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-sm p-12 text-center shadow-sm border border-slate-200">
            <Calendar className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 font-public-sans text-lg mb-6">You don't have any bookings yet.</p>
            <Button
              onClick={() => navigate('/')}
              className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-8 py-3"
              data-testid="book-now-button"
            >
              Book Your First Trip
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div
                key={booking.booking_id}
                className="bg-white rounded-sm p-6 shadow-sm border border-slate-200"
                data-testid={`booking-${booking.booking_id}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono font-bold text-primary text-lg">Booking #{booking.booking_id.slice(-8)}</span>
                      <span
                        className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wide ${
                          booking.booking_status === 'confirmed'
                            ? 'bg-green-100 text-green-700'
                            : booking.booking_status === 'pending'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                        data-testid={`booking-status-${booking.booking_id}`}
                      >
                        {booking.booking_status}
                      </span>
                      <span
                        className={`px-3 py-1 rounded-sm text-xs font-semibold uppercase tracking-wide ${
                          booking.payment_status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        Payment: {booking.payment_status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span className="font-public-sans">{booking.passengers.length} passengers</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span className="font-public-sans">Seats: {booking.booked_seats.join(', ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-public-sans">Contact: {booking.contact_phone}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right ml-6">
                    <p className="font-manrope text-2xl font-bold text-secondary mb-3">${booking.total_amount.toFixed(2)}</p>
                    {booking.booking_status === 'confirmed' && (
                      <Button
                        onClick={() => handleCancelBooking(booking.booking_id)}
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm px-4 py-2 text-xs"
                        data-testid={`cancel-booking-${booking.booking_id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserDashboard;